import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { Alert, Badge, Button, Card, Input, PageHeader } from '../components/ui';

export default function Settings() {
  const { user, refresh } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setDisplayName(user?.displayName ?? '');
  }, [user]);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.updateProfile(displayName.trim() || null);
      await refresh();
      setMsg({ kind: 'success', text: 'Profile saved.' });
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiError ? err.message : 'Save failed' });
    } finally {
      setBusy(false);
    }
  }

  if (!user) return null;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Your account profile and identity." />

      <div className="max-w-xl space-y-6">
        <Card>
          <h3 className="mb-4 font-semibold text-slate-900">Profile</h3>
          {msg && (
            <div className="mb-3">
              <Alert kind={msg.kind}>{msg.text}</Alert>
            </div>
          )}
          <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
          <Input value={user.email} disabled className="mb-4 bg-slate-50" />
          <label className="mb-1 block text-sm font-medium text-slate-700">Display name</label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" />
          <div className="mt-4">
            <Button onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save profile'}
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">GitHub identity</h3>
            {user.github ? <Badge color="blue">@{user.github}</Badge> : <Badge>not linked</Badge>}
          </div>
          <p className="mt-2 text-sm text-slate-500">
            {user.github
              ? 'Your GitHub handle is linked. Published manifests must declare this handle as the author.'
              : 'Link GitHub to verify authorship and qualify for the verified-author tier.'}
          </p>
          {!user.github && (
            <a href={api.githubLinkUrl()} className="mt-4 inline-block">
              <Button variant="secondary">Link GitHub account</Button>
            </a>
          )}
        </Card>

        <Card>
          <h3 className="font-semibold text-slate-900">Account</h3>
          <dl className="mt-3 space-y-1 text-sm">
            <Row label="Role" value={user.role} />
            <Row label="Publishing" value={user.canPublish ? 'enabled' : 'not enabled'} />
            <Row label="Member since" value={new Date(user.createdAt).toLocaleDateString()} />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}
